import { segment } from "@mobi/libraries/analytics"
import { currencyToFloat } from "@mobi/utils/formatters/currency"

//Utils
import { getUUIDFromUrl } from "utils/browser"
import { getFromSessionStorage } from "utils/storage"

const mountSegmentPayload = (payload = {}) => {
  return {
    ...payload,
    leadUUID: getUUIDFromUrl()?.value,
  }
}

const tracking = {
  pageLoad: ({ step } = {}) => {
    const isClosedFlow =
      getFromSessionStorage({ name: "isClosedFlow" }) || false
    segment.page("iCarros page", mountSegmentPayload({ step, isClosedFlow }))
  },

  buttonClickSegment: ({ buttonName, buttonText, location }) => {
    segment.track(
      `iCarros Button clicked`,
      mountSegmentPayload({
        buttonName,
        buttonText,
        location,
      }),
    )
  },

  leadStart: (payload) => {
    segment.track(
      "iCarros Lead Start",
      mountSegmentPayload({
        fullName: payload.fullName,
        customer_type: "cpf",
        leadUUID: payload.uuid,
      }),
    )
  },

  leadStartFechada: (payload) => {
    // TODO: incluir as seguintes infos: fullName, gearDescription, plateLastNumber, colorDescription
    segment.track(
      "iCarros Lead Start Sim Fechada",
      mountSegmentPayload({
        brand: payload.vehicleDealProperties.brandModelVersion,
        category: payload.vehicleDealProperties.isNewVehicle ? "Zero" : "Usado",
        price: payload.vehicleDealProperties.vehicleValue,
        productId: payload.vehicleDealProperties.dealId,
        km: payload.vehicleDealProperties.vehicleKm,
      }),
    )
  },

  vehicleDealProperties: (payload) => {
    segment.track(
      "iCarros Lead Step Completed: Vehicles Data",
      mountSegmentPayload({
        initialPrice: payload.initialPrice,
        finalPrice: payload.finalPrice,
        initialYear: payload.initialYear,
        finalYear: payload.finalYear,
        brand: payload.brand,
        model: payload.model,
        city: payload.city,
        purchasePeriod: payload.purcePeriod,
      }),
    )
  },

  vehiclesAds: (payload) => {
    segment.track(
      "iCarros Lead Step Completed: Vehicles Ads",
      mountSegmentPayload({
        brand: payload.brand,
        category: payload.category,
        price: payload.price,
        productId: payload.productId,
        gearDescription: payload.gearDescription,
        km: payload.km,
        plateLastNumber: payload.plateLastNumber,
        colorDescription: payload.colorDescription,
      }),
    )
  },

  adsListView: (payload) => {
    const { filters, deals, currentPage } = payload

    segment.track(
      "iCarros Ads List View",
      mountSegmentPayload({
        currentPage,
        filters: {
          brand: filters.brand,
          model: filters.model,
          initialYear: filters.initialYear,
          finalYear: filters.finalYear,
          minKm: filters.minKm,
          maxKm: filters.maxKm,
          finalPlate: filters.finalPlate,
          isNewVehicle: filters.isNewVehicle,
          city: filters.city,
          kmRange: filters.kmRange,
          initialPrice: filters.initialPrice,
          finalPrice: filters.finalPrice,
          sort: filters.sort,
        },
        ads: deals.map((deal, index) => ({
          brand: deal.trimDescription,
          isZeroKm: deal.isZeroKm,
          price: deal.price,
          productId: deal.dealID.toString(),
          position: index + 1,
        })),
      }),
    )
  },

  purchasePeriod: (payload) => {
    segment.track(
      "iCarros Lead Step Completed Sim Fechada: Purchase Period",
      mountSegmentPayload({
        purchasePeriod: payload.purchasePeriod,
      }),
    )
  },

  verificationCodeConfirmation: (payload) => {
    const isClosedFlow =
      getFromSessionStorage({ name: "isClosedFlow" }) || false
    const eventPayload = {
      notifyMethod: payload.notifyMethod,
    }

    if (isClosedFlow) {
      segment.track(
        "iCarros Lead Step Completed Sim Fechada: verification-code-confirmation",
        mountSegmentPayload(eventPayload),
      )
    } else {
      segment.track(
        "iCarros Lead Step Completed: verification-code-confirmation",
        mountSegmentPayload(eventPayload),
      )
    }
  },

  simulationFail: (payload) => {
    const isClosedFlow =
      getFromSessionStorage({ name: "isClosedFlow" }) || false

    const eventPayload = {
      ads: (payload.deals || []).map((deal, index) => ({
        brand: deal.trimDescription,
        isZeroKm: deal.isZeroKm,
        price: deal.price,
        productId: deal.dealID.toString(),
        position: index + 1,
      })),
    }

    if (isClosedFlow) {
      segment.track(
        "iCarros Lead Not Approved Sim Fechada",
        mountSegmentPayload(eventPayload),
      )
    } else {
      segment.track(
        "iCarros Lead Not Approved",
        mountSegmentPayload(eventPayload),
      )
    }
  },

  simulationSent: () => {
    const isClosedFlow =
      getFromSessionStorage({ name: "isClosedFlow" }) || false

    if (isClosedFlow) {
      segment.track(
        "iCarros Simulation Sent Sim Fechada",
        mountSegmentPayload(),
      )
    } else {
      segment.track("iCarros Simulation Sent", mountSegmentPayload())
    }
  },

  simulationSuccess: (payload) => {
    const isClosedFlow =
      getFromSessionStorage({ name: "isClosedFlow" }) || false

    const installment = (payload?.simulationInstallments || []).find(
      (item) => item.selected || item.suggested,
    )

    if (!installment) {
      return
    }

    const eventPayload = {
      annualEffectiveInterest: installment.annualEffectiveInterest,
      annualInterest: installment.annualInterest,
      contractRegistrationFee: currencyToFloat(payload.contractRegistrationFee),
      defaultDownPayment: currencyToFloat(payload.defaultDownPayment),
      downPayment: currencyToFloat(payload.downPayment),
      highChanceOfApproval: installment.highChanceOfApproval,
      installmentAmount: installment.amount,
      insurance: payload.financeProtectionInsurance || false,
      insuranceValue: installment.financeInsuranceInstallmentValue,
      iofValue: installment.iofValue,
      isDefaultDownPaymentSuggested:
        currencyToFloat(payload.downPayment) ===
        currencyToFloat(payload.defaultDownPayment),
      minAcceptableDownPayment: payload.simulation.minAcceptableDownPayment,
      minDownPaymentForHighChanceApproval:
        payload.simulation.highChanceOfApprovalDownPayment,
      monthlyEffectiveInterest: installment.monthlyEffectiveInterest,
      monthlyInterest: installment.monthlyInterest,
      numberOfInstallments: installment.installment,
      propertyValuationFee: currencyToFloat(payload.propertyValuationFee),
      registrationFee: currencyToFloat(payload.simulation.registrationFee),
      suggested: installment.suggested,
      totalAmountFinanced: currencyToFloat(payload.totalAmountFinanced),
      totalAmountFinancedWithoutTax: currencyToFloat(
        payload.totalAmountFinancedWithoutTax,
      ),
      totalPayable: currencyToFloat(payload.totalPayable),
    }

    if (isClosedFlow) {
      segment.track(
        "iCarros Lead Approved Sim Fechada",
        mountSegmentPayload(eventPayload),
      )
    } else {
      segment.track("iCarros Lead Completed", mountSegmentPayload(eventPayload))
    }
  },

  simulationUpdate: (payload) => {
    const isClosedFlow =
      getFromSessionStorage({ name: "isClosedFlow" }) || false

    const installment = payload.simulationInstallments.find(
      (item) => item.selected || item.suggested,
    )
    const eventPayload = {
      annualEffectiveInterest: installment.annualEffectiveInterest,
      annualInterest: installment.annualInterest,
      contractRegistrationFee: currencyToFloat(
        payload.simulation.contractRegistrationFee,
      ),
      defaultDownPayment: currencyToFloat(
        payload.simulation.defaultDownPayment,
      ),
      downPayment: currencyToFloat(payload.simulation.downPayment),
      highChanceOfApproval: installment.highChanceOfApproval,
      installmentAmount: installment.amount,
      insurance: payload.simulation.financeProtectionInsurance || false,
      insuranceValue: installment.financeInsuranceInstallmentValue,
      iofValue: installment.iofValue,
      isDefaultDownPaymentSuggested:
        currencyToFloat(payload.simulation.downPayment) ===
        currencyToFloat(payload.simulation.defaultDownPayment),
      minAcceptableDownPayment: payload.simulation.minAcceptableDownPayment,
      minDownPaymentForHighChanceApproval:
        payload.simulation.highChanceOfApprovalDownPayment,
      monthlyEffectiveInterest: installment.monthlyEffectiveInterest,
      monthlyInterest: installment.monthlyInterest,
      numberOfInstallments: installment.installment,
      propertyValuationFee: currencyToFloat(
        payload.simulation.propertyValuationFee,
      ),
      registrationFee: currencyToFloat(payload.simulation.registrationFee),
      suggested: installment.suggested,
      totalAmountFinanced: currencyToFloat(
        payload.simulation.totalAmountFinanced,
      ),
      totalAmountFinancedWithoutTax: currencyToFloat(
        payload.simulation.totalAmountFinancedWithoutTax,
      ),
      totalPayable: currencyToFloat(payload.simulation.totalPayable),
    }

    if (isClosedFlow) {
      segment.track(
        "iCarros Update of Simulation Conditions Sim Fechada",
        mountSegmentPayload(eventPayload),
      )
    } else {
      segment.track(
        "iCarros Update of Simulation Conditions",
        mountSegmentPayload(eventPayload),
      )
    }
  },

  proposalSent: () => {
    const isClosedFlow =
      getFromSessionStorage({ name: "isClosedFlow" }) || false

    if (isClosedFlow) {
      segment.track("iCarros Proposal Sent Sim Fechada", mountSegmentPayload())
    } else {
      segment.track("iCarros Proposal Sent", mountSegmentPayload())
    }
  },

  proposalNotApproved: () => {
    const isClosedFlow =
      getFromSessionStorage({ name: "isClosedFlow" }) || false

    if (isClosedFlow) {
      segment.track(
        "iCarros Proposal Not Approved Sim Fechada",
        mountSegmentPayload(),
      )
    } else {
      segment.track("iCarros Proposal Not Approved", mountSegmentPayload())
    }
  },

  proposalWaitingStatus: () => {
    const isClosedFlow =
      getFromSessionStorage({ name: "isClosedFlow" }) || false

    if (isClosedFlow) {
      segment.track(
        "iCarros Proposal Waiting Status Sim Fechada",
        mountSegmentPayload(),
      )
    } else {
      segment.track("iCarros Proposal Waiting Status", mountSegmentPayload())
    }
  },

  proposalApproved: () => {
    const isClosedFlow =
      getFromSessionStorage({ name: "isClosedFlow" }) || false

    if (isClosedFlow) {
      segment.track(
        "iCarros Proposal Approved Sim Fechada",
        mountSegmentPayload(),
      )
    } else {
      segment.track("iCarros Proposal Approved", mountSegmentPayload())
    }
  },

  sellerPhoneChecked: () => {
    const isClosedFlow =
      getFromSessionStorage({ name: "isClosedFlow" }) || false

    if (isClosedFlow) {
      segment.track(
        "iCarros Proposal Seller Phone Checked Sim Fechada",
        mountSegmentPayload(),
      )
    } else {
      segment.track(
        "iCarros Proposal Seller Phone Checked",
        mountSegmentPayload(),
      )
    }
  },

  maritalStatusChanged: ({ maritalStatus, partnerName, partnerDocument }) => {
    segment.track(
      "iCarros Marital Changed",
      mountSegmentPayload({
        maritalStatus: maritalStatus,
        isPartnerNameTyped: !!partnerName,
        isPartnerDocumentTyped: !!partnerDocument,
      }),
    )
  },

  proposalPartnerFormSubmit: ({
    maritalStatus,
    partnerName,
    partnerDocumentNumber,
  }) => {
    segment.track(
      "iCarros Proposal Partner Form Send",
      mountSegmentPayload({
        maritalStatus: maritalStatus,
        isPartnerNameTyped: !!partnerName,
        isPartnerDocumentTyped: !!partnerDocumentNumber,
      }),
    )
  },
}

export default tracking
